import { api } from '../client';

export interface BankCashAccountDto {
  id: string;
  code: string;
  name: string;
  type: string;
  description?: string;
  balance: number;
  status: string;
}

export interface BankCashTransactionDocumentDto {
  id: string;
  name: string;
  mimeType: string | null;
  size: number;
}

export interface BankCashTransactionDto {
  id: string;
  accountId: string;
  accountName: string;
  accountCode: string;
  type: 'deposit' | 'withdrawal';
  amount: number;
  description: string;
  date: string;
  reference: string;
  documents?: BankCashTransactionDocumentDto[];
}

export const bankCashApi = {
  async getAccounts(): Promise<{ data?: BankCashAccountDto[]; message?: string }> {
    return api.get('/bank-cash/accounts') as Promise<{ data: BankCashAccountDto[] }>;
  },

  async getTransactions(params?: {
    account_id?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<{ data?: BankCashTransactionDto[]; message?: string }> {
    const sp = new URLSearchParams();
    if (params?.account_id) sp.set('account_id', params.account_id);
    if (params?.from_date) sp.set('from_date', params.from_date);
    if (params?.to_date) sp.set('to_date', params.to_date);
    const q = sp.toString() ? `?${sp.toString()}` : '';
    return api.get(`/bank-cash/transactions${q}`) as Promise<{ data: BankCashTransactionDto[] }>;
  },

  async getTransaction(id: string): Promise<{ data?: BankCashTransactionDto; message?: string }> {
    return api.get(`/bank-cash/transactions/${id}`) as Promise<{ data: BankCashTransactionDto }>;
  },

  async createTransaction(data: {
    account_id: string;
    type: 'deposit' | 'withdrawal';
    amount: number;
    description?: string;
    date: string;
  }): Promise<{ data?: BankCashTransactionDto; message?: string; errors?: Record<string, string[]> }> {
    return api.post('/bank-cash/transactions', data) as Promise<{
      data: BankCashTransactionDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async updateTransaction(
    id: string,
    data: {
      account_id?: string;
      type?: 'deposit' | 'withdrawal';
      amount?: number;
      description?: string;
      date?: string;
    }
  ): Promise<{ data?: BankCashTransactionDto; message?: string; errors?: Record<string, string[]> }> {
    return api.put(`/bank-cash/transactions/${id}`, data) as Promise<{
      data: BankCashTransactionDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async deleteTransaction(id: string): Promise<{ success?: boolean; message?: string }> {
    return api.delete(`/bank-cash/transactions/${id}`) as Promise<{ success?: boolean; message?: string }>;
  },

  async uploadDocument(transactionId: string, file: File): Promise<{ data?: BankCashTransactionDocumentDto; message?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const headers: HeadersInit = { Accept: 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${api.baseURL}/bank-cash/transactions/${transactionId}/documents`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return res.json();
  },

  async downloadDocument(documentId: string, fileName: string): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${api.baseURL}/bank-cash/documents/${documentId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'document';
    a.click();
    URL.revokeObjectURL(url);
  },
};
